import React, { FormEvent } from "react";
import { Package, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { ValidationErrors } from "src/errors";
import {
  ReviewPurchaseOrderConfirmationFormData,
  ReviewPurchaseOrderConfirmationFormDataKey,
  PurchaseOrder,
  ReviewPurchaseOrderConfirmationFormAction
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { PurchaseOrderConfirmationSection } from "src/components/sections/purchaseOrders";
import { ProductConfirmationTable } from "src/components/tables";
import { TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
// import { PurchaseOrderService } from "src/services";

interface ReviewPurchaseOrderConfirmationFormProps {
  formData: ReviewPurchaseOrderConfirmationFormData;
  purchaseOrderToReview?: PurchaseOrder;
  validationErrors?: ValidationErrors<ReviewPurchaseOrderConfirmationFormDataKey>;
  formLvlErrorMessage: string;
  action: ReviewPurchaseOrderConfirmationFormAction;
  handleInputChange: (
    field: ReviewPurchaseOrderConfirmationFormDataKey,
    value: ReviewPurchaseOrderConfirmationFormData[keyof ReviewPurchaseOrderConfirmationFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: ReviewPurchaseOrderConfirmationFormAction) => void;
}

const ReviewPurchaseOrderConfirmationForm: React.FC<ReviewPurchaseOrderConfirmationFormProps> = ({
  formData,
  purchaseOrderToReview,
  validationErrors,
  formLvlErrorMessage,
  action,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const { confirmationDetails } = purchaseOrderToReview ?? {};

  // Check if backorder checkbox should be shown
  // const shouldShowBackorderCheckbox = useMemo(() => {
  //   if (!purchaseOrderToReview || !confirmationDetails?.products || !purchaseOrderToReview.products) {
  //     return false;
  //   }

  //   // Check if PO has portal access (backorder scheduling only for portal vendors)
  //   const hasPortalAccess = !!purchaseOrderToReview.portalAccess;
  //   if (!hasPortalAccess) {
  //     return false;
  //   }

  //   // Check if PO is original (backorders cannot have backorders)
  //   const isOriginal = PurchaseOrderService.isOriginalPurchaseOrder(purchaseOrderToReview);
  //   if (!isOriginal) {
  //     return false;
  //   }

  //   // Check if any products have restock dates (indicating backorder will be created)
  //   const hasProductsWithRestockDates = confirmationDetails.products.some(
  //     (productConfirmation) => productConfirmation.estRestockDate && !productConfirmation.isDiscontinued
  //   );

  //   return hasProductsWithRestockDates;
  // }, [purchaseOrderToReview, confirmationDetails]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Review Confirmation</h1>
            <p className="text-gray-300 mt-2 text-lg">Review and approve vendor purchase order confirmations</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order Search
                </h3>
                <TextInputBox
                  id="purchase-order-number"
                  placeholder="Purchase Order Number"
                  onChange={(value) => handleInputChange("purchaseOrderId", value.toUpperCase())}
                  value={formData.purchaseOrderId}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {purchaseOrderToReview && confirmationDetails && (
                <>
                  {/* Products & Confirmation Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase">
                      Product Confirmations
                    </h3>
                    <ProductConfirmationTable
                      products={purchaseOrderToReview.products ?? []}
                      productConfirmations={purchaseOrderToReview.confirmationDetails?.products ?? []}
                    />
                  </div>

                  {/* Confirmation Details Section */}
                  <PurchaseOrderConfirmationSection
                    confirmationStatus={purchaseOrderToReview.confirmationStatus}
                    confirmedAt={purchaseOrderToReview.confirmedAt}
                    confirmationDetails={purchaseOrderToReview.confirmationDetails}
                    confirmationNote={purchaseOrderToReview.additionalInfo?.confirmationNote}
                  />
                </>
              )}

              {/* Form Level Error */}
              {action !== "CANCEL_CONFIRMATION" && (
                <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
              )}

              {/* Backorder Creation Checkbox */}
              {/* {shouldShowBackorderCheckbox && (
                <div className="border border-primary-200 rounded-2xl p-5 bg-primary-50">
                  <h3 className="text-sm font-semibold text-primary-900 mb-3 tracking-wide uppercase flex items-center">
                    <Package className="w-4 h-4 mr-2" />
                    Backorder Purchase Order
                  </h3>
                  <div className="bg-white border border-primary-200 rounded-lg p-4">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.createBackorderPO}
                        onChange={(e) => handleInputChange("createBackorderPO", e.target.checked)}
                        className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          Create backorder purchase order for missing quantities
                        </p>
                        <p className="text-sm text-gray-600">
                          A backorder purchase order(s) will be automatically created with the missing quantities,
                          grouped by their restock dates.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )} */}

              {/* Approve Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                  disabled={!purchaseOrderToReview && !formData.purchaseOrderId}
                  onClick={() => setAction(!purchaseOrderToReview ? "FETCH" : "APPROVE_CONFIRMATION")}
                  type="submit"
                >
                  {!purchaseOrderToReview ? (
                    <>
                      <Package className="w-4 h-4" />
                      <span>Search</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Approve Confirmation</span>
                    </>
                  )}
                </button>

                {purchaseOrderToReview && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <AlertCircle className="w-4 h-4 text-primary-500" />
                    <span>Vendor will be notified via email</span>
                  </div>
                )}
              </div>

              {/* Rejection Section */}
              {purchaseOrderToReview && (
                <div className="border border-error-200 rounded-2xl p-5 bg-error-50">
                  <h3 className="text-sm font-semibold text-error-800 mb-4 tracking-wide uppercase flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Confirmation
                  </h3>
                  <TextInputBoxLarge
                    id="cancellation-reason"
                    placeholder="Cancellation Reason"
                    onChange={(value) => handleInputChange("cancellationReason", value)}
                    value={formData.cancellationReason}
                    maxLength={650}
                  />
                  <ValidationErrorMessage
                    message={validationErrors?.getErrorMessageByFormDataKey("cancellationReason")}
                  />
                  <div className="mt-4">
                    <button
                      className="px-6 py-3 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                      onClick={() => setAction("CANCEL_CONFIRMATION")}
                      type="submit"
                      disabled={!formData.cancellationReason}
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Reject & Cancel Order</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Form Level Error for Cancellation */}
              {action === "CANCEL_CONFIRMATION" && (
                <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { ReviewPurchaseOrderConfirmationForm };
